<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'
    import type { Writable } from 'svelte/store'

    import { isEqual } from 'lodash-es'
    import { DateTime } from 'luxon'

    import { gettext as _, ngettext, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import * as rt from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import PageList from 'hydria/components/PageList.svelte'

    import 'codemirror/mode/javascript/javascript'

    import Icon from '@iconify/svelte'

    import { sgoto, sreplace } from 'hydria/svelte_helpers'
    import { querystring } from 'hydria/utils/route'

    import CodeMirror from 'admin/components/CodeMirror.svelte'

    const LIMIT = 10

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        skip: 'int1+',
        seen: 'string',
    }
    const mappingOrder = ['skip']

    // Search options for the backend
    interface SOptions {
        skip: number
        seen: '' | 'all'
    }
    const defaults: SOptions = {
        skip: 0,
        seen: '',
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/cira-messages' : `/cira-messages?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/cira-messages' : `/cira-messages?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string | undefined) {
        let sMap = rt.simpleParseQs(qs ? qs : '', rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    interface IItem {
        id: string
        ctime: DateTime
        cira_time: DateTime
        original_string: string
        original_string_lg: 'fr' | 'en'
        cira_message_id: string

        message_code: number
        message_data: { [key: string]: any }

        seen: boolean
        seen_time: DateTime
    }
    let results: Writable<IItem[]> = writable([])

    let selectedItem: IItem | undefined = $state(undefined)
    let showRaw = false

    let updating = $state(false)
    let loading = true
    let searchId = 0
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        if (sOptions.seen !== 'all') sdata.seen = false
        // for (const [field, value] of Object.entries(sOptions)) {
        //     if (['skip', 'seen'].includes(field)) continue
        //     switch (mapping[field]) {
        //         case '3state':
        //         case 'boolean':
        //             if (value != null) sdata[field] = value
        //             break
        //         case 'string':
        //             if (value != null && value != '') sdata[field] = value
        //             break
        //     }
        // }

        searchId++
        ;((localSearchId) => {
            if (selectedItem) selectedItem = undefined
            if (showRaw) showRaw = false

            simpleFetch('GET', '/json/general/1/cira-messages', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)
    let unseenIds: string[] = $state([])

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        let uIds: string[] = []

        const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            for (const key of ['ctime', 'cira_time', 'seen_time']) {
                if (item[key]) item[key] = toLuxon(item[key])
            }
            litems.push(item)
            if (!item.seen) uIds.push(item.id)
        }

        results.set(litems)
        unseenIds = uIds
        if (loading) loading = false
    }

    let working = $state(false)

    async function setSeen(nv: boolean) {
        if (!selectedItem) return

        working = true
        try {
            const r = await simpleFetch(nv ? 'PUT' : 'DELETE', `/json/general/1/cira-message/${selectedItem.id}/seen`)
            doSearch()
        } catch (e) {
            console.error('Error:', e)
            notify({
                type: 'danger',
                title: 'Seen',
                text: `Le message n’a pas pu être marqué « ${nv ? 'vu' : 'non-vu'} »`,
            })
        }

        working = false
    }

    async function seenVisible() {
        if (!unseenIds.length || working || loading) return

        working = true
        try {
            const r = await simpleFetch('POST', `/json/general/1/cira-message/seen-many`, { msg_ids: $state.snapshot(unseenIds) })
            doSearch()
        } catch (e) {
            console.error('Error:', e)
            notify({
                type: 'danger',
                title: 'Seen',
                text: `Le(s) message(s) n’ont pas pu être marqués « vu »`,
            })
        }

        working = false
    }
</script>

<Breadcrumb crumbs={[{ txt: tgettext('CIRA Messages') }]} />

<div class="card mb-4">
    <div class="card-title text-primary">
        <div>{$_('Search parameters')}</div>
        <div>
            <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                disabled={loading || working || unseenIds.length < 1}
                onclick={() => {
                    seenVisible()
                }}>{$ngettext('Cacher {n} message', 'Cacher {n} messages', { n: unseenIds.length })}</button
            >
        </div>
    </div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
            }}
            class="form-inline"
        >
            <div class="form-group">
                <label for="seen-unseen">Filter:</label>
                <div class="btn-group">
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        disabled={updating}
                        class:active={sOptions.seen === 'all'}
                        onclick={() => {
                            sOptions.seen = 'all'
                            setQueryString(sOptions)
                        }}>All</button
                    >
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        disabled={updating}
                        class:active={sOptions.seen === ''}
                        onclick={() => {
                            sOptions.seen = ''
                            setQueryString(sOptions)
                        }}>Unseen</button
                    >
                </div>
            </div>
        </form>
    </div>
</div>

<div class="row">
    <div class="col-lg-7 col-md-7 col-sm-6">
        <div class="card">
            <div class="card-title text-primary">{$_('Messages')}</div>

            <div class="card-body">
                <div class="table-responsive">
                    <table class="table" class:disabled-look={updating}>
                        <thead>
                            <tr>
                                <th class="col-md-1">Seen</th>
                                <th class="col-md-2">Time</th>
                                <th>Message</th>
                            </tr>
                        </thead>
                        {#if $results.length}
                            <tbody>
                                {#each $results as item (item.id)}
                                    <tr
                                        onclick={() => {
                                            selectedItem = selectedItem === item ? undefined : item
                                        }}
                                    >
                                        <td class:active={selectedItem === item}>
                                            {#if item.seen}
                                                <Icon inline icon="fa:check" />
                                            {/if}
                                        </td>
                                        <td style="white-space: nowrap" class:active={selectedItem === item}><Date value={item.cira_time} format="h-table-s" /></td>
                                        <td class:text-warning={!item.seen} class:active={selectedItem === item}>{item.original_string}</td>
                                    </tr>
                                {/each}
                            </tbody>
                        {:else if updating}
                            <tbody class="animated-loading">
                                <tr>
                                    <td></td>
                                    <td><div class="text-line" style="width: 90%"></div></td>
                                    <td>
                                        <div class="text-line" style="width: 75%"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td><div class="text-line" style="width: 90%"></div></td>
                                    <td>
                                        <div class="text-line" style="width: 90%"></div>
                                        <div class="text-line" style="width: 25%"></div>
                                    </td>
                                </tr>
                            </tbody>
                        {/if}
                    </table>
                </div>
            </div>
            <div class="card-footer">
                <PageList
                    loading={updating}
                    {nItems}
                    limit={LIMIT}
                    skip={sOptions.skip}
                    setSkip={(e) => {
                        sOptions.skip = e
                        setQueryString(sOptions)
                    }}
                />
            </div>
        </div>
    </div>
    <div class="col-lg-5 col-md-5 col-md-6">
        <div class="card card-default">
            <div class="card-title text-primary">{$_('Information')}</div>
            <div class="card-body">
                {#if selectedItem == null}
                    Choisir une ligne dans la table de gauche.
                {:else}
                    <!-- FIXME: CIRA ne semble plus envoyer de codes depuis FURY?
                        {#if selectedItem.message_code == 3029}
                        arst
                    {:else}
                        <p class="text-warning">Message non supporté, voir « raw » ci-dessous.</p>
                    {/if} -->
                    <p>{selectedItem.original_string}</p>
                    <p class="clearfix">
                        {#if working}
                            <button class="btn btn-info float-end" type="button" disabled
                                ><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Un moment…</button
                            >
                        {:else if selectedItem.seen}
                            <button class="btn btn-outline-secondary float-end" type="button" disabled={working} onclick={() => setSeen(false)}>Mark as not seen</button>
                        {:else}
                            <button class="btn btn-primary float-end" type="button" disabled={working} onclick={() => setSeen(true)}>Mark as seen</button>
                        {/if}
                    </p>
                    {#if Object.keys(selectedItem.message_data).length}
                        <hr />
                        <h4>Raw message data:</h4>
                        <CodeMirror options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: true, mode: { name: 'javascript', json: true } }} value={JSON.stringify(selectedItem.message_data, null, 2)} />
                    {/if}
                {/if}
            </div>
        </div>
    </div>
</div>
